import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function AdminRoutesReport() {
  const { t } = useTranslation();

  const [routes, setRoutes] = useState([]);
  const [textFilter, setTextFilter] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/routes`);
      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch admin routes report", err);
      setRoutes([]);
      setMessage("❌ " + t("admin_routes_report.messages.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const translateStatus = (status) => {
    if (!status) return "-";
    return t(`admin_routes_report.status_values.${status}`, status);
  };

  const getText = (value) => (value == null ? "" : String(value).toLowerCase());

  const filteredRoutes = routes.filter((route) => {
    const search = textFilter.toLowerCase().trim();

    return (
      search === "" ||
      getText(route.route_id).includes(search) ||
      getText(route.vendor_id).includes(search) ||
      getText(route.bus_id).includes(search) ||
      getText(route.driver_id).includes(search) ||
      getText(route.route_name).includes(search) ||
      getText(route.start_point).includes(search) ||
      getText(route.end_point).includes(search) ||
      getText(route.status).includes(search) ||
      getText(route.notes).includes(search)
    );
  });

  return (
    <StandardScreen
      topNav={<TopNav title={t("admin_routes_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("admin_routes_report.title")}>
              <Stack gap="3">
                <FormField label={t("admin_routes_report.fields.text_filter.label")}>
                  <TextInput
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                    placeholder={t("admin_routes_report.fields.text_filter.placeholder")}
                  />
                </FormField>
              </Stack>
            </Card>

            <StatusMessage
              message={loading ? t("admin_routes_report.messages.loading") : message}
            />

            <Card>
              <TableShell maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>
                      <Th columnIndex={0} sticky wider>
                        {t("admin_routes_report.table.route_id")}
                      </Th>

                      <Th columnIndex={1} sticky wider>
                        {t("admin_routes_report.table.route_name")}
                      </Th>

                      <Th wider>{t("admin_routes_report.table.vendor_id")}</Th>
                      <Th wider>{t("admin_routes_report.table.bus_id")}</Th>
                      <Th wider>{t("admin_routes_report.table.driver_id")}</Th>
                      <Th wider>{t("admin_routes_report.table.start_point")}</Th>
                      <Th wider>{t("admin_routes_report.table.end_point")}</Th>
                      <Th wider>{t("admin_routes_report.table.status")}</Th>
                      <Th wider>{t("admin_routes_report.table.created_at")}</Th>
                      <Th>{t("admin_routes_report.table.notes")}</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRoutes.map((route) => (
                      <tr key={route.route_id}>
                        <Td columnIndex={0} sticky>
                          {route.route_id}
                        </Td>

                        <Td columnIndex={1} sticky wider>
                          {route.route_name || "-"}
                        </Td>

                        <Td>{route.vendor_id || "-"}</Td>
                        <Td>{route.bus_id || "-"}</Td>
                        <Td>{route.driver_id || "-"}</Td>
                        <Td>{route.start_point || "-"}</Td>
                        <Td>{route.end_point || "-"}</Td>
                        <Td>{translateStatus(route.status)}</Td>
                        <Td>{formatDateTime(route.created_at)}</Td>
                        <Td>{route.notes || "-"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableShell>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}